import cx from 'classnames'
import React, { ChangeEvent, KeyboardEvent } from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faA,
  faCircleHalfStroke,
  faImage,
  faQuestionCircle,
  faMagic,
  faMoon,
  faSignOutAlt,
  faSun,
  faVolumeDown,
  faVolumeUp,
  faHourglassEnd,
  faEye,
  faEyeSlash,
  faTowerBroadcast,
} from '@fortawesome/free-solid-svg-icons'

import { activateNextPane } from '../actions/play-actions'
import { toggleChannelBroadcast } from '../actions/social/update-play-followers'
import {
  cycleLyricsTextColor,
  nextBackgroundEffect,
  nextBackgroundImage,
} from '../actions/settings-actions'
import {
  BLEND_MODES,
  DEFAULT_FONT_SIZE,
  HOW_TO_PROMPT,
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  Pane,
  PaneClasses,
} from '../constants/constants'
import { useAppDispatch, useAppSelector } from '../hooks'
import { selectIsFocus } from '../reducers/focusSlice'
import modalsSlice from '../reducers/modalsSlice'
import sessionSlice from '../reducers/sessionSlice'
import { selectIsCurrUserBroadcasting } from '../selectors/blaster-peer-selectors'
import { selectIsPlaying } from '../selectors/current-play-selectors'
import {
  selectCurrentBackgroundBlendMode,
  selectCurrentThemeIndex,
  selectIsFullScreen,
  selectMatchInvites,
  getModeFlags,
  selectMode,
  getAudienceFlags,
  selectAudience,
} from '../selectors/session-selectors'
import { selectViewSettings } from '../selectors/settings-selectors'
import trackMixer from '../services/TrackMixer'
import userManager from '../services/UserManager'
import { isMobile as getIsMobile } from '../util/track-utils'
import ModeSwitch from './play-area/ModeSwitch'
import themes from './themes'
import AudienceSwitch from './widgets/AudienceSwitch'
import GamerSwitch from './widgets/GamerSwitch'

const Footer = ({ currentUsername = '??' }) => {
  const isMobile = getIsMobile()
  const dispatch = useAppDispatch()
  const matchInvites = useAppSelector(selectMatchInvites)
  const isCurrFocus = useAppSelector(selectIsFocus(Pane.FOOTER))
  const theme = useAppSelector(selectCurrentThemeIndex)
  const blendMode = BLEND_MODES[useAppSelector(selectCurrentBackgroundBlendMode)]
  const isFullscreen = useAppSelector(selectIsFullScreen)
  const isPlaying = useAppSelector(selectIsPlaying)
  const { isPlayMode } = getModeFlags(useAppSelector(selectMode))
  const audience = getAudienceFlags(useAppSelector(selectAudience))
  const { isShowLyrics } = useAppSelector(selectViewSettings(0))
  const isBroadcasting = useAppSelector(selectIsCurrUserBroadcasting)
  const isShowBreakTimer = !isPlayMode ? true : !isPlaying && (audience.isPub || audience.isCouch)
  const openInviteCount = Object.keys(matchInvites).reduce((acc, matchOwner) => {
    const ownerInvites = matchInvites[matchOwner]
    return acc + Object.keys(ownerInvites).length
  }, 0)
  const triggerHelp = () => {
    dispatch(modalsSlice.actions.toggleHelpModal(true))
  }

  const onToggleTimer = () => {
    dispatch(modalsSlice.actions.toggleBreakTimerModal(true))
  }
  const onHideLyricsToggle = () => {
    trackMixer.toggleMasked(!isShowLyrics)
  }
  const volumeRef = React.createRef<HTMLInputElement>()
  const textSizeRef = React.createRef<HTMLInputElement>()

  const onBackgroundSettings = () => {
    dispatch(nextBackgroundImage(1))
  }
  const onToggleLightDarkTheme = () => {
    const nextThemeIndex = (theme + 1) % themes.length
    dispatch(sessionSlice.actions.setCurrentThemeIndex(nextThemeIndex))
  }
  const onNextBgBlendMode = () => {
    dispatch(nextBackgroundEffect())
  }
  const _getVolumeControl = () => {
    const onChange = (event: ChangeEvent<HTMLInputElement>) =>
      (trackMixer.volume = parseFloat(event.target.value))
    const setVolume = (newVolume: number) => {
      trackMixer.volume = newVolume
      if (volumeRef.current) {
        volumeRef.current.value = String(newVolume)
      }
    }
    const onMuteClick = () => setVolume(0)
    const onMaxClick = () => setVolume(10)
    const onKeyDown = (event: KeyboardEvent) => {
      const step = event.keyCode === 38 ? 1 : event.keyCode === 40 ? -1 : 0
      setVolume(trackMixer.volume + step)
      event.stopPropagation()
    }
    return (
      <div className="footerSlider volume">
        <button onClick={onMuteClick}>
          <FontAwesomeIcon size="sm" icon={faVolumeDown as IconProp} title="Volume Down" />
        </button>
        <input
          ref={volumeRef}
          type="range"
          step="1"
          min="0"
          max="10"
          defaultValue={trackMixer.volume}
          onChange={onChange}
          onInput={onChange}
          onKeyDown={onKeyDown}
        />
        <button onClick={onMaxClick}>
          <FontAwesomeIcon size="sm" icon={faVolumeUp as IconProp} title="Volume Up" />
        </button>
      </div>
    )
  }
  const _getTextSizeControl = () => {
    const onChange = (event: ChangeEvent<HTMLInputElement>) =>
      (trackMixer.defaultGamer.lyricFontSize = parseInt(event.target.value))

    const setFontSize = (newFontSize: number) => {
      trackMixer.defaultGamer.lyricFontSize = newFontSize
      if (textSizeRef.current) {
        textSizeRef.current.value = String(newFontSize)
      }
    }
    const onClick = () => setFontSize(DEFAULT_FONT_SIZE)
    const onKeyDown = (event: KeyboardEvent) => {
      const step = event.keyCode === 38 ? 1 : event.keyCode === 40 ? -1 : 0
      setFontSize(trackMixer.defaultGamer.lyricFontSize + step)
      event.stopPropagation()
    }
    return (
      <div className="footerSlider fontSize">
        <button onClick={onClick}>
          <FontAwesomeIcon size="sm" icon={faA as IconProp} title="Lyric font size" />
        </button>
        <input
          ref={textSizeRef}
          type="range"
          min={MIN_FONT_SIZE}
          max={MAX_FONT_SIZE}
          step="1"
          defaultValue={trackMixer?.defaultGamer?.lyricFontSize || DEFAULT_FONT_SIZE}
          onChange={onChange}
          onInput={onChange}
          onKeyDown={onKeyDown}
        />
      </div>
    )
  }
  const _getTextColorControl = () => {
    return (
      <button className="footerButton" onClick={() => dispatch(cycleLyricsTextColor())}>
        <FontAwesomeIcon
          icon={faCircleHalfStroke as IconProp}
          title={`Toggle Lyrics Color`}
          size={'sm'}
        />
      </button>
    )
  }
  const _getLogout = () => {
    const logout = () => {
      userManager.logout()
    }
    return (
      <button className="footerButton" aria-label="logout" onClick={logout}>
        <FontAwesomeIcon size="lg" icon={faSignOutAlt as IconProp} title="Logout" />
      </button>
    )
  }
  const _getUserBadge = () => {
    const onClick = () => {
      dispatch(modalsSlice.actions.toggleUserSettings(true))
    }
    return (
      <button className="footerButton" aria-label="user" onClick={onClick} title="Preferences">
        <img src={userManager.picture} alt="avatar" />
        <span>{currentUsername}</span>
        {openInviteCount > 0 && <div className="badge">{openInviteCount}</div>}
      </button>
    )
  }
  const className = cx(PaneClasses[Pane.FOOTER], { hasFocus: isCurrFocus })
  const onClick = () => {
    // console.log(`container: ${PaneClasses[Pane.FOOTER]}`)
    dispatch(activateNextPane({ pane: Pane.FOOTER }))
  }
  const onToggleBroadcasting = () => {
    dispatch(toggleChannelBroadcast())
  }
  return (
    <div className={className} onClick={onClick}>
      <div className="footerLeft">
        <AudienceSwitch />
        {!isMobile && !isPlaying && <ModeSwitch />}
        {!isMobile && audience.isProducer && <GamerSwitch />}
      </div>
      <div className="footerCenter">
        <div className="volumeSettings">{_getVolumeControl()}</div>

        <div className="imageSettings">
          <button className="footerButton" aria-label="background" onClick={onBackgroundSettings}>
            <FontAwesomeIcon icon={faImage as IconProp} title="Switch Background" />
          </button>
          <button className="footerButton" aria-label="theme" onClick={onToggleLightDarkTheme}>
            <FontAwesomeIcon
              size="sm"
              icon={(theme === 0 ? faSun : faMoon) as IconProp}
              title="Switch Theme"
            />
          </button>
          <div className="blendMode">
            <button className="footerButton" aria-label="theme" onClick={onNextBgBlendMode}>
              <FontAwesomeIcon size="sm" icon={faMagic as IconProp} title="Switch Effect" />
            </button>
            <span>{blendMode}</span>
          </div>
        </div>
        <div className="textSettings">
          {_getTextSizeControl()}
          {_getTextColorControl()}
          <button className="footerButton" onClick={onHideLyricsToggle}>
            <FontAwesomeIcon
              icon={(isShowLyrics ? faEye : faEyeSlash) as IconProp}
              title={`${isShowLyrics ? 'Hide' : 'Show'} Lyrics`}
              size={theme === 0 ? 'lg' : 'sm'}
            />
          </button>
        </div>
      </div>
      <div className="footerRight">
        {isShowBreakTimer && (
          <button className="footerButton" onClick={onToggleTimer}>
            <FontAwesomeIcon icon={faHourglassEnd as IconProp} title="Break Timer" />
          </button>
        )}
        {!isPlaying && (
          <button id="info" className="footerButton" aria-label="help" onClick={triggerHelp}>
            <FontAwesomeIcon
              size="lg"
              className="img"
              icon={faQuestionCircle as IconProp}
              title="Help"
            />
            {isFullscreen && <span className="help">{HOW_TO_PROMPT}</span>}
          </button>
        )}
        {!isMobile && !audience.isFollow && (
          <button className="footerButton" onClick={onToggleBroadcasting}>
            <FontAwesomeIcon
              icon={faTowerBroadcast as IconProp}
              color={isBroadcasting ? 'red' : 'gray'}
              title={`${isShowLyrics ? 'Start' : 'Stop'} Broadcasting`}
              size="lg"
            />
          </button>
        )}
        {!(audience.isPub && isFullscreen) && _getUserBadge()}
        {!(audience.isPub && isFullscreen) && _getLogout()}
      </div>
    </div>
  )
}

export default Footer
